import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { useLocalStorageState } from "../hooks/useLocalStorageState";
import FullPage from "../components/ui/FullPage";
import Spinner from "../components/ui/Spinner";

type ProtectedRouteProps = {
  requiredPermission?: string;
  redirectTo?: string;
  children: JSX.Element;
  roles?: Array<string>;
};

function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const [searchParams] = useSearchParams();
  const [currentUserId] = useLocalStorageState("currentUserId");
  const [isReady, setIsReady] = useState(false);

  const {
    user,
    userRole,
    clientName,
    isLoading,
    isSubClientAssignedPermissionLoading,
  } = useAuth();
  const isAuthenticated = user?.isAuthenticated || false;

  const redirectUrl = !currentUserId ? "/" : `/?token=${currentUserId}`;

  // ✅ Store loginUrl in localStorage if present
  // ✅ Store client details in sessionStorage
  useEffect(() => {
    const userLoginUrl = searchParams.get("loginUrl");
    if (userLoginUrl && !localStorage.getItem("userLoginUrl")) {
      localStorage.setItem("userLoginUrl", userLoginUrl);
    }

    const clientId = searchParams.get("clientId");

    if (clientId) {
      const clientData = {
        clientId,
        clientLogoUrl: searchParams.get("clientLogoUrl"),
        clientName: searchParams.get("clientName"),
        isClient: searchParams.get("isClient") === "true",
        needToShowClientLogo:
          searchParams.get("needToShowClientLogo") === "true",
        role: searchParams.get("isClient") === "true" ? "client" : "subClient",
      };

      sessionStorage.setItem(
        "currentSelectedClient",
        JSON.stringify(clientData),
      );
    }
  }, [searchParams]);

  // ✅ Update document title
  useEffect(() => {
    if (clientName) {
      document.title = clientName;
    }
  }, [clientName]);

  useEffect(() => {
    const delayTimer = setTimeout(() => {
      setIsReady(true);
    }, 400);

    return () => clearTimeout(delayTimer);
  }, []);

  const isLoadingState =
    isLoading || isSubClientAssignedPermissionLoading || !isReady;
  // ✅ Safe check for roles
  const userHasRequiredRole =
    isAuthenticated && (roles?.length ? roles.includes(userRole) : true);

  // ✅ Show spinner while loading
  if (isLoadingState) {
    return (
      <FullPage>
        <Spinner />
      </FullPage>
    );
  }

  // ✅ Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectUrl} replace />;
  }

  // ✅ Redirect if user does not have required role
  if (!userHasRequiredRole) {
    return <Navigate to="/something-went-wrong" replace />;
  }

  return children;
}

export default ProtectedRoute;
